import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';

// The shared Tarawa feedback overlay (the one on Ravens Retreat and SculptDecor), adapted to
// Jabberr: no opt-in query parameter and no second login. The widget talks to /feedback/... on this
// origin; the api proxies to the shared intake service over the bridge with the session user as the
// actor. Off entirely when the api has no FEEDBACK_BRIDGE_KEY; ?feedback=0 hides it for the tab.
// A person can put it away with the x on the launcher and bring it back with the floating pill.
declare global {
  interface Window {
    TarawaFeedback?: {
      mount(options: { projectId: string; apiBase: string; useAppSession: boolean }): Promise<{ destroy(): void } | null>;
    };
  }
}

const HIDDEN_KEY = 'jabberr.feedback-hidden';

/**
 * The shared widget docks its Feedback pill in a corner, bottom right unless the person has moved it. The
 * bottom right is where the composer's send button lives, so the bottom-left corner is seeded when the person
 * has not chosen one; the widget reads it from localStorage (key built in its feedback.js:
 * tarawa-feedback-corner:<apiBase>:<project>). shell.css keeps that strip of the sidebar free while the
 * widget is mounted.
 */
function seedLauncherCorner(project: string): void {
  const key = `tarawa-feedback-corner:/feedback:${project}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, 'bl');
  } catch {
    // Storage unavailable: the widget falls back to its own default.
  }
}

/**
 * Injected into the widget's shadow root. The x hides while the panel or a hint is open (important: it beats
 * the button's inline all:unset). On phones the widget stretches its fixed dock across the whole bottom edge
 * even when only the pill shows, and the empty part of that strip swallowed taps on the page under it; only
 * the dock's visible children take pointer events here.
 */
const SHADOW_STYLE =
  '.dock{pointer-events:none}.dock>*{pointer-events:auto}' +
  '.dock:has(> .panel:not([hidden])) > .jabberr-feedback-close,.dock:has(> .hint:not([hidden])) > .jabberr-feedback-close{display:none!important}';

/** Puts the x on the pill's top-right corner. The dock is the fixed offset parent of both. */
function placeCloseButton(pill: HTMLElement, btn: HTMLElement): void {
  btn.style.left = `${pill.offsetLeft + pill.offsetWidth - 12}px`;
  btn.style.top = `${pill.offsetTop - 8}px`;
}

function readHidden(): boolean {
  try {
    return localStorage.getItem(HIDDEN_KEY) === '1';
  } catch {
    return false;
  }
}

function persistHidden(val: boolean): void {
  try {
    localStorage.setItem(HIDDEN_KEY, val ? '1' : '0');
  } catch {
    /* */
  }
}

/** The close control we inject into the widget's launcher. The widget builds and re-renders its
 *  shadow DOM on its own schedule, so this must survive losing the button; the watcher below
 *  re-adds it whenever it goes missing. */
function buildCloseButton(onClose: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'jabberr-feedback-close';
  btn.type = 'button';
  btn.title = 'Put feedback away';
  btn.setAttribute('aria-label', 'Put the feedback launcher away');
  btn.textContent = '×';
  btn.style.cssText =
    // Absolute on the fixed dock, out of its flow; placeCloseButton puts it on the pill's corner.
    'all:unset;position:absolute;z-index:1;box-sizing:border-box;cursor:pointer;width:20px;height:20px;line-height:18px;text-align:center;border-radius:50%;' +
    'color:#475569;background:#fff;border:1px solid #cbd5e1;box-shadow:0 1px 3px rgba(15,23,42,0.2);font-size:13px;pointer-events:auto;';
  btn.addEventListener('mouseenter', () => (btn.style.background = '#e2e8f0'));
  btn.addEventListener('mouseleave', () => (btn.style.background = '#fff'));
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  });
  return btn;
}

export function FeedbackOverlay() {
  const [hidden, setHidden] = useState<boolean>(() => {
    const params = new URLSearchParams(window.location.search);
    try {
      if (params.get('feedback') === '0') {
        sessionStorage.setItem('jabberr.feedback-off', '1');
        return true;
      }
      if (params.get('feedback') === '1') sessionStorage.removeItem('jabberr.feedback-off');
      else if (sessionStorage.getItem('jabberr.feedback-off') === '1') return true;
    } catch {
      // Storage-disabled browsers just get the overlay.
    }
    // On mobile, start hidden unless explicitly restored (stored value is '0').
    if (window.matchMedia('(max-width: 800px)').matches) {
      try {
        const stored = localStorage.getItem(HIDDEN_KEY);
        if (stored === '0') return false;
        return true;
      } catch { /* */ }
      return true;
    }
    return readHidden();
  });
  const handleRef = useRef<{ destroy(): void } | null>(null);
  const hiddenRef = useRef(hidden);
  hiddenRef.current = hidden;
  const readyRef = useRef(false);

  const putAway = useCallback((): void => {
    persistHidden(true);
    setHidden(true);
    document.body.classList.remove('feedback-visible');
  }, []);

  const mountWidget = useCallback(async (): Promise<void> => {
    let config;
    try {
      config = await api.feedback.config();
    } catch {
      return;
    }
    if (!config.enabled || !config.base || !config.project) return;
    try {
      if (!window.TarawaFeedback) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = `${config.base}/client/feedback.js`;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('unavailable'));
          document.head.append(script);
        });
      }
    } catch {
      return; // A missing review service never disrupts Jabberr itself.
    }
    try {
      handleRef.current?.destroy();
      seedLauncherCorner(config.project);
      const handle = (await window.TarawaFeedback?.mount({ projectId: config.project, apiBase: '/feedback', useAppSession: true })) ?? null;
      handleRef.current = handle;
    } catch {
      // A failed mount leaves the application usable.
    }
  }, []);

  useEffect(() => {
    if (hidden || !readyRef.current) return;
    void mountWidget();
  }, [hidden, mountWidget]);

  // Watches the dock for the whole time the widget is up: whenever the x is missing (first build, a widget
  // re-render, or a remount), put it back. Cheap and self-healing.
  useEffect(() => {
    if (hidden) return;
    const tick = (): void => {
      try {
        // The visible pill sits in .dock (position fixed, styled by the widget's own stylesheet). The x is a
        // sibling of the pill, never inside it (a button in a button), on its corner (placeCloseButton).
        const shadow = document.getElementById('tarawa-feedback')?.shadowRoot;
        const dock = shadow?.querySelector('.dock');
        const pill = dock?.querySelector<HTMLElement>(':scope > .pill');
        if (!shadow || !dock || !pill) return;
        if (!shadow.querySelector('style[data-jabberr]')) {
          const style = document.createElement('style');
          style.setAttribute('data-jabberr', '');
          style.textContent = SHADOW_STYLE;
          shadow.append(style);
        }
        let btn = dock.querySelector<HTMLElement>(':scope > .jabberr-feedback-close');
        if (!btn) {
          btn = buildCloseButton(() => {
            handleRef.current?.destroy();
            handleRef.current = null;
            putAway();
          });
          dock.appendChild(btn);
        }
        // Every tick, so the x follows the pill when it is dragged to another corner or the window resizes.
        placeCloseButton(pill, btn);
      } catch {
        // The launcher stays without a close control; ?feedback=0 still hides it for the tab.
      }
    };
    tick();
    const timer = setInterval(tick, 500);
    return () => clearInterval(timer);
  }, [hidden, putAway]);

  useEffect(() => {
    readyRef.current = true;
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 800px)').matches;
    const shouldMount = isMobile
      ? localStorage.getItem(HIDDEN_KEY) === '0'
      : !readHidden();
    if (shouldMount) {
      document.body.classList.add('feedback-visible');
      void mountWidget();
    }
    return () => {
      handleRef.current?.destroy();
      handleRef.current = null;
      document.body.classList.remove('feedback-visible');
    };
  }, [mountWidget]);

  if (!hidden) return null;
  return (
    <button
      type="button"
      className="feedback-restore"
      title="Bring the feedback launcher back"
      aria-label="Bring the feedback launcher back"
      onClick={() => {
        persistHidden(false);
        setHidden(false);
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
      </svg>
      <span>Feedback</span>
    </button>
  );
}
